import { useRef, useState, type DragEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, errorMessage } from "../../api/client";
import { useImports } from "../../api/hooks";
import { Chip, Empty, IMPORT_STATUS, Icon, PageHead, Spinner, useToast } from "../../components/ui";
import { dateTime, fmt } from "../../lib/format";

const ACCEPT = ".csv,.txt,.tsv,.xlsx,.xls";

export function ImportsPage() {
  const list = useImports();
  const [over, setOver] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const nav = useNavigate();
  const qc = useQueryClient();

  async function upload(file: File) {
    setUploading(file.name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api<{ id: number; duplicate_file: boolean }>("/imports", { method: "POST", body: fd });
      if (r.duplicate_file) toast("Ce fichier a déjà été importé : voici son rapport.");
      qc.invalidateQueries({ queryKey: ["imports"] });
      nav(`/master-data/imports/${r.id}`);
    } catch (e) {
      toast(errorMessage(e), true);
    } finally {
      setUploading(null);
    }
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files[0];
    if (f) upload(f);
  };

  return (
    <main className="page">
      <PageHead eyebrow="Master Data" title="Imports" />
      <label className={`drop${over ? " over" : ""}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}
        tabIndex={0} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), input.current?.click())}>
        <span className="ic"><Icon name="upload" /></span>
        <span className="stack" style={{ gap: 2 }}>
          <b style={{ fontSize: 17 }}>{uploading ? `Envoi de « ${uploading} »…` : "Déposer un fichier ou cliquer pour le choisir"}</b>
          <span className="muted">CSV, Excel (.xlsx, .xls) ou export Google Sheets. Chaque fichier enrichit la même Master Data : entreprises rapprochées par SIRET, nouvelles colonnes conservées, rien n'est écrasé sans trace.</span>
        </span>
        {uploading && <Spinner />}
        <input ref={input} type="file" accept={ACCEPT} className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
      </label>

      <div className="tile flush">
        <div className="scroll">
          <table className="table">
            <thead><tr><th>Fichier</th><th>État</th><th className="r">Lignes</th><th>Déposé le</th><th>Terminé le</th></tr></thead>
            <tbody>
              {list.data?.map((i) => {
                const st = IMPORT_STATUS[i.status] ?? { label: i.status, tone: "gray" as const };
                return (
                  <tr key={i.id}>
                    <td><Link to={`/master-data/imports/${i.id}`}><b>{i.file}</b></Link> <span className="faint">#{i.id}</span></td>
                    <td><Chip tone={st.tone} dot solid={i.status === "failed"}>{st.label}</Chip></td>
                    <td className="r num">{fmt(i.rows)}</td>
                    <td>{dateTime(i.created_at)}</td>
                    <td>{dateTime(i.finished_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {list.isLoading && <div className="empty"><Spinner /></div>}
          {list.data?.length === 0 && <Empty title="Aucun import pour l'instant">Le premier fichier construit la base mère.</Empty>}
        </div>
      </div>
    </main>
  );
}
